import http from './client'
import type {
  ResponseData,
  LoginData,
  RegisterData,
  EmployeePageQuery,
  Department,
  DepartmentResponse,
  CreateDepartmentFormData,
  UpdateDepartmentFormData,
  WeChatResponse,
  selfInfo,
  QueryUserEmployee,
  Organization,
  MemberPermissionVO,
  MemberPermissionCommand,
  UpdateStaffRoleListFormData,
  Role,
  UpdateEmployeeCommand,
  OrganizationVO,
  EmployeeVo,
} from './ram.d'

export interface PaginationQuery {
  pageIndex: number
  pageSize?: number
}

export interface PaginationResData<T> extends PaginationQuery {
  totalCount: number
  totalPage: number
  data: T
}

export const weChatQueryConfig = () =>
  http<{
    // 应用唯一标识，在微信开放平台提交应用审核通过后获得
    appid: string
    // 应用授权作用域，拥有多个作用域用逗号（,）分隔，网页应用目前仅填写snsapi_login即可
    scope: string
    // 重定向地址，需要进行UrlEncode
    redirect_uri: string
    // 用于保持请求和回调的状态，授权请求后原样带回给第三方。该参数可用于防止csrf攻击（跨站请求伪造攻击），建议第三方带上该参数，可设置为简单的随机数加session进行校验
    state?: string
    // 提供"black"、"white"可选，默认为黑色文字描述。详见文档底部FAQ
    theme?: string
    // 自定义样式链接，第三方可根据实际需求覆盖默认样式。详见文档底部FAQ
    href?: string
    // true：手机点击确认登录后可以在 iframe 内跳转到 redirect_uri，false：手机点击确认登录后可以在 top window 跳转到 redirect_uri。默认为 false。
    self_redirect?: boolean
    style?: string
  }>({
    url: '/ram/api/wechat/query-config',
  })

export const weChatVerificationConfig = () =>
  http<{
    // 应用唯一标识，在微信开放平台提交应用审核通过后获得
    appid: string
    // 应用授权作用域，拥有多个作用域用逗号（,）分隔，网页应用目前仅填写snsapi_login即可
    scope: string
    // 重定向地址，需要进行UrlEncode
    redirect_uri: string
    // 用于保持请求和回调的状态，授权请求后原样带回给第三方。该参数可用于防止csrf攻击（跨站请求伪造攻击），建议第三方带上该参数，可设置为简单的随机数加session进行校验
    state?: string
    // 提供"black"、"white"可选，默认为黑色文字描述。详见文档底部FAQ
    theme?: string
    // 自定义样式链接，第三方可根据实际需求覆盖默认样式。详见文档底部FAQ
    href?: string
    // true：手机点击确认登录后可以在 iframe 内跳转到 redirect_uri，false：手机点击确认登录后可以在 top window 跳转到 redirect_uri。默认为 false。
    self_redirect?: boolean
    style?: string
  }>({
    url: '/ram/api/wechat/query-scan-verification-config',
  })

export const checkWechatScanVerification = () => {
  return http<ResponseData>({
    url: '/ram/api/account/check-wechat-scan-verification',
  })
}

export const checkBindingWechatVerification = (config?: { hideToast?: boolean }) => {
  return http<boolean>({
    url: '/ram/api/account/check-binding-wechat-verification',
    ...config,
  })
}

export const userLogin = (data: LoginData, config?: { hideToast?: boolean }) => {
  return http<ResponseData>({
    url: '/ram/api/user/login',
    data,
    ...config,
  })
}

export const userRegister = (data: RegisterData, config?: { hideToast?: boolean }) => {
  return http<ResponseData>({
    url: '/ram/api/user/register',
    data,
    ...config,
  })
}

export const verificationCode = (data: { phone?: string; email?: string }) => {
  return http({
    url: '/ram/api/sms/send-verification-code',
    data,
  })
}

export const generateInvitedToken = (hour?: number) => {
  return http({
    url: '/ram/api/employee/generate-invited-token',
    data: { hour },
  })
}

export const updateInvitedTokenExpiryTime = (data: { token: string; hour: number }) => {
  return http({
    url: '/ram/api/employee/update-invited-token-expiry-time',
    data,
  })
}

export const getMemberList = (data: any) => {
  return http<PaginationResData<EmployeePageQuery[]>>({
    url: '/ram/api/employee/list-employee-by-page',
    data,
  })
}

export const updateEmployee = (data: UpdateEmployeeCommand) => {
  return http({
    url: '/ram/api/employee/update-employee',
    data,
  })
}

export const getDepartmentTreeData = (data?: {
  departmentName?: string
  containEmployee?: boolean
}) => {
  return http<DepartmentResponse>({
    url: '/ram/api/department/list-department-tree',
    data: data || {},
  })
}

export const createDepartment = (data: CreateDepartmentFormData) => {
  return http<Department>({ url: '/ram/api/department/create-department', data })
}

export const updateDepartment = (data: UpdateDepartmentFormData) => {
  return http<Department>({ url: '/ram/api/department/update-department', data })
}

export const deleteDepartment = (data: { id: number }) => {
  return http({ url: '/ram/api/department/delete-department', data })
}

export const deleteEmployee = (data: { id: number; receiverEmployeeId: number }) => {
  return http({ url: '/ram/api/employee/delete-employee', data })
}

export const exitOrganization = (
  data: { receiverEmployeeId: number },
  config?: { hideToast?: boolean },
) => {
  return http<number>({ url: '/ram/api/employee/exit-organization', data, ...config })
}

export const updateDepartmentOrder = (data: {
  id: number
  parentId: number | undefined
  sequence: number
}) => {
  return http({ url: '/ram/api/department/update-department-sequence', data })
}

export const querySelfInfo = () => {
  return http<selfInfo>({ url: '/ram/api/account/query-account' })
}
export const bindEmail = (
  data: { email: string; verificationCode?: string },
  config?: { hideToast?: boolean },
) => {
  return http({ url: '/ram/api/account/bind-email', data, ...config })
}
export const updatePhoneInfo = (
  data: { phone: string; verificationCode: string },
  config?: { hideToast?: boolean },
) => {
  return http({ url: '/ram/api/account/bind-phone', data, ...config })
}
export const updatePassword = (data: {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}) => {
  return http({
    url: '/ram/api/account/update-password',
    data,
  })
}
export const updateNameInfo = (data: { name: string }) => {
  return http({ url: '/ram/api/account/update-name', data })
}

export const updateUserName = (data: { userName: string }) => {
  return http({ url: '/ram/api/account/update-user-name', data })
}

export const verifyUserNameExist = (data: { userName: string }) => {
  return http<boolean>({ url: '/ram/api/account/verify-user-name-exist', data })
}
export const updateGenderInfo = (data: { gender: string }) => {
  return http({ url: '/ram/api/account/update-gender', data })
}

export const updateAvatar = (avatar: string) => {
  return http({ url: '/ram/api/account/update-avatar', data: { avatar } })
}
export const updateCart = (card: string) => {
  return http({ url: '/ram/api/account/update-card', data: { card } })
}
export const unbindWeChat = () => {
  return http({ url: '/ram/api/account/unbind-wechat' })
}

export const queryBindConfig = () => {
  return http<WeChatResponse>({ url: '/ram/api/wechat/query-bind-config' })
}

export const checkCode = (data: { phone?: string; code?: string }) => {
  return http({ url: '/common/api/sms/check-phone-match-verification-code', data })
}

export const updateEmployeeName = (data: { employeeName: string }) => {
  return http({ url: '/ram/api/account/update-employee-name', data })
}

export const queryUserEmployeeInfo = () => {
  return http<QueryUserEmployee>({
    url: '/ram/api/user/query-user-employee-info',
  })
}

export const queryLoginInfo = () => {
  return http<ResponseData>({
    url: '/ram/api/user/query-login-info',
    loading: false,
    hideToast: true,
  })
}

export const settingUserInfo = (data: { username: string; password: string }) => {
  return http({
    url: '/ram/api/user/setting-username-password',
    data,
  })
}

export const queryInviteOrganization = (token: string) => {
  return http({
    url: `/ram/api/employee/query-invited-organization?invitedToken=${token}`,
  })
}

export const changeOrganization = (organizationId: number) => {
  return http<ResponseData>({
    url: '/ram/api/user/change-organization',
    data: {
      organizationId,
    },
  })
}

export const listOrganization = () => {
  return http<Organization[]>({
    url: '/ram/api/organization/list-organization-by-user-id',
  })
}

export function createOrganization(data: { organizationName: string }) {
  return http<Organization[]>({
    url: '/ram/api/organization/create-organization',
    data,
  })
}

export function queryOrganizationMemberByUserId(userId: number) {
  return http<MemberPermissionVO>({
    url: '/ram/api/organization-member/query-organization-member-by-user-id',
    data: { userId },
  })
}

export function updateOrganizationMember(data: MemberPermissionCommand) {
  return http({
    url: '/ram/api/organization-member/update-organization-member',
    data,
  })
}

export function listEmployeeRole(userId: number) {
  return http<Role[]>({
    url: '/ram/api/role/list-employee-role',
    data: { userId },
  })
}

export function queryEmployee(userId: number, organizationId?: number) {
  return http<any>({
    url: '/ram/api/employee/query-employee',
    data: { userId, organizationId },
  })
}

export function updateEmployeeRole(data: UpdateStaffRoleListFormData) {
  return http({
    url: '/ram/api/role/update-employee-role',
    data,
  })
}

export function batchUpdateEmployeeRole(data: UpdateStaffRoleListFormData[]) {
  return Promise.all(data.map(d => updateEmployeeRole(d)))
}

export function updateEmployeeRole2(data: { userId: number; roleId: number }) {
  return updateEmployeeRole({
    userId: data.userId,
    employeeRoleList: [
      {
        roleId: data.roleId,
        roleName: '',
        delegate: false,
      },
    ],
  })
}

export function batchUpdateEmployeeRole2(data: { userId: number; roleId: number }[]) {
  return Promise.all(data.map(d => updateEmployeeRole2(d)))
}

export function listDepartmentEmployeeTree() {
  return http<DepartmentResponse>({
    url: '/ram/api/department/list-department-employee-tree',
    data: {},
  })
}

export function logout(data: { token: string }) {
  return http({
    url: '/ram/api/user/logout',
    data,
  })
}

export const queryCurrentOrganization = () => {
  return http<OrganizationVO>({ url: '/ram/api/organization/query-current-organization' })
}

export const updateOrganizationLogo = (data: { logo: string }) => {
  return http<OrganizationVO>({ url: '/ram/api/organization/update-organization-logo', data })
}

export const updateOrganizationName = (data: { name: string }) => {
  return http<OrganizationVO>({ url: '/ram/api/organization/update-organization-name', data })
}

export const transferOrganizationOwner = (
  data: { organizationId: number; userId: number },
  config?: { hideToast?: boolean },
) => {
  return http<OrganizationVO>({ url: '/ram/api/organization/transfer-owner', data, ...config })
}

export const deleteOrganization = (data: { id: number }, config?: { hideToast?: boolean }) => {
  return http<any>({ url: '/ram/api/organization/delete-organization', data, ...config })
}

/**
 * 解散团队
 */
export const dissolveOrganization = (config?: { hideToast?: boolean }) => {
  return http<number>({ url: '/ram/api/organization/dissolve-organization', ...config })
}

export const listEmployeeByOrganizationId = (data: { organizationId: number }) => {
  return http<EmployeeVo[]>({
    url: '/ram/api/employee/list-employee-by-organization-id',
    data: { ...data, stateId: 'normal' },
  })
}

export const getEmployeeOrganizationRole = (data: { userId: number }) => {
  return http<{ userId: number; role: string; roleName: string }>({
    url: '/ram/api/employee/get-employee-organization-role',
    data,
  })
}

export const bindWechatAccount = (data?: {
  phone?: string
  email?: string
  verificationCode?: string
  ascSessionId?: string
  ascSig?: string
  ascToken?: string
}) => {
  return http<ResponseData>({
    url: '/ram/api/user/bind-wechat-acocunt',
    method: 'POST',
    data: data ?? {},
  })
}

export const checkAccountPassword = (data: { userName: string; password: string }) => {
  return http({
    url: '/ram/api/account/check-account-password',
    data,
  })
}

export const checkAccountPhone = (data: { phone: string; verificationCode: string }) => {
  return http({
    url: '/ram/api/account/check-account-phone',
    data,
  })
}

export const checkAccountEmail = (data: { email: string; verificationCode: string }) => {
  return http({
    url: '/ram/api/account/check-account-email',
    data,
  })
}

export const updateOrganizationPreference = (preferenceJson: string) => {
  return http({
    url: '/ram/api/organization/update-organization-preference',
    data: { preferenceJson },
  })
}

export const getOrganizationPreference = () => {
  return http<{ preferenceJson: string }>({
    url: '/ram/api/organization/get-organization-preference',
  })
}

export const updateUserPreference = (preferenceJson: string) => {
  return http({
    url: '/ram/api/user/update-user-preference',
    data: { preferenceJson },
  })
}

export const getUserPreference = () => {
  return http<{ preferenceJson: string }>({
    url: '/ram/api/user/get-user-preference',
  })
}

export const getUserRemark = (data: { userId: number }) => {
  return http<{ remark?: string }>({
    url: '/ram/api/platform-user/get-user-remark',
    data,
  })
}

export const updateUserRemark = (data: { userId: number; remark?: string }) => {
  return http({
    url: '/ram/api/platform-user/update-user-remark',
    data,
  })
}

export const getUserTrackingRecord = (data: { userId: number }) => {
  return http<{ trackingRecord?: string }>({
    url: '/ram/api/platform-user/get-user-tracking-record',
    data,
  })
}

export const updateUserTrackingRecord = (data: { userId: number; trackingRecord?: string }) => {
  return http({
    url: '/ram/api/platform-user/update-user-tracking-record',
    data,
  })
}
